<template>
  <div class="balance-card">
    <div class="balance-card-header">
      <div class="balance-card-title">
        {{ displayTitle }}
      </div>
      <ToolTip
        v-if="showInfoIcon && tooltipText"
        placement="top-start"
        :arrow="false"
        :content="tooltipText"
      >
        <div class="balance-card-info-icon">
          <img src="../../app/assets/images/info.svg" alt="info icon" />
        </div>
      </ToolTip>
    </div>
    <div class="balance-card-amount-container">
      <div class="balance-card-amount" :class="{ 'balance-card-amount-negative': isNegativeAmount }">
        {{ formattedAmount }}
      </div>
      <div v-if="hasPendingAmount" class="pending-amount-tag-container">
        <div class="pending-amount-tag">
          {{ formattedPendingAmount }} {{ $t('balanceReserve.pending') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Formatter from '@/app/utils/common/functions/formatter';
import ToolTip from '@/components/common/ToolTip.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'BalanceCard',
  components: {
    ToolTip
  },
  props: {
    balanceAccount: {
      type: Object, // MerchantBalanceAccountResponseDto
      required: true
    },
    showInfoIcon: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters('app', ['getMerchantCurrency']),
    displayTitle() {
      return this.$t(`balanceReserve.accountTypes.${this.balanceAccount.type}`);
    },
    displayAmount() {
      return this.balanceAccount.amount || 0;
    },
    displayPendingAmount() {
      return this.balanceAccount.pending_amount ?? 0;
    },
    hasPendingAmount() {
      return (this.balanceAccount.pending_amount ?? 0) > 0;
    },
    isNegativeAmount() {
      return this.displayAmount < 0;
    },
    displayCurrency() {
      return this.balanceAccount.currency || this.getMerchantCurrency;
    },
    formattedAmount() {
      return Formatter.format({
        type: 'currency',
        data: this.displayAmount,
        currencyCode: this.displayCurrency
      });
    },
    formattedPendingAmount() {
      return Formatter.format({
        type: 'currency',
        data: this.displayPendingAmount,
        currencyCode: this.displayCurrency
      });
    },
    tooltipText() {
      return this.$t(`balanceReserve.accountTypesTooltip.${this.balanceAccount.type}`);
    }
  }
}
</script>

<style scoped lang="scss">
.balance-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: $space_sm;
  width: 100%;
  max-width: 300px;
  background: $base_white;
  border-radius: $radius_lg;
  border: 1px solid $neutral_200;
  flex: 1;
  
  .balance-card-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: $space_mi;
    
    .balance-card-title {
      font-family: $ff_sans;
      font-weight: $fw_medium;
      font-size: $fs_caption;
      line-height: $lh_st;
      color: $neutral_600;
      margin-right: $space_ti;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
    }
    
    .balance-card-info-icon {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      cursor: pointer;
      color: $neutral_400;
      
      &:hover {
        color: $neutral_600;
      }
    }
  }
  
  
  .balance-card-amount-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: $space_sm;
    flex-wrap: nowrap;
    width: 100%;
  }
  
  .balance-card-amount {
    font-family: $ff_sans;
    font-weight: $fw_bolder;
    font-size: $fs_lead;
    line-height: $lh_sm;
    color: $neutral_1000;
    flex-shrink: 1;
    min-width: 0;

    &.balance-card-amount-negative {
      color: $danger_600;
    }
  }
  
  .pending-amount-tag-container {
    display: flex;
    text-align: left;
    flex-direction: row;
    justify-content: flex-start;
    flex-shrink: 0;
  }
  
  .pending-amount-tag {
    font-size: 10px;
    padding: 2px 10px;
    border-radius: 10px;
    height: fit-content;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    background-color: $warn_50;
    color: $warn_800;
    border: 1px solid $warn_200;
    white-space: nowrap;
  }
}
</style>
